import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiUrl } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building2, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { parseSkSidlo } from "@shared/parse-sk-sidlo";
import { cn } from "@/lib/utils";

/** Ugyanaz mint Deal.tsx — Produkty / kontakt adatkártya */
const DEAL_DETAIL_DATA_CARD =
  "rounded-lg border border-border bg-card p-3 text-sm";

interface DealCompaniesSectionProps {
  dealId: string;
}

export function DealCompaniesSection({ dealId }: DealCompaniesSectionProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [icoLoad, setIcoLoad] = useState("");
  const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null);
  const [editingIco, setEditingIco] = useState("");

  const companiesQuery = useQuery({
    queryKey: ["deal-companies", dealId],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/companies`), { credentials: "include" });
      if (!r.ok) return { companies: [] };
      const j = await r.json();
      return j as { companies: any[] };
    },
    enabled: !!dealId,
  });

  const loadFromFinstatMutation = useMutation({
    mutationFn: async () => {
      const digits = icoLoad.replace(/\D/g, "").trim();
      if (digits.length < 8) {
        throw new Error(t("deal.icoInvalid", "Zadajte platné IČO (8–10 číslic)."));
      }
      const lookup = await fetch(apiUrl("/api/crm/finstat-lookup"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ico: digits }),
      });
      const fin = await lookup.json().catch(() => ({}));
      if (!lookup.ok || !fin.success) {
        throw new Error(fin.message || fin.error || t("deal.finstatNotFound", "Firma sa nenašla v registri."));
      }
      const sidlo = typeof fin.sidlo_firmy === "string" ? fin.sidlo_firmy : "";
      const parsed = parseSkSidlo(sidlo);
      const add = await fetch(apiUrl(`/api/crm/deals/${dealId}/companies`), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ico: fin.ico ?? digits,
          companyName: fin.meno_firmy ?? digits,
          address: parsed.street || null,
          city: parsed.city || null,
          country: "Slovensko",
          dic: fin.dic ?? null,
          ic_dph: fin.ic_dph ?? null,
        }),
      });
      const added = await add.json().catch(() => ({}));
      if (!add.ok) {
        throw new Error(added.message || added.error || t("deal.addCompanyFailed", "Nepodarilo sa pridať firmu."));
      }
      return { added, isActive: fin.is_active !== false, status: fin.status as string | undefined };
    },
    onSuccess: (result) => {
      setIcoLoad("");
      qc.invalidateQueries({ queryKey: ["deal-companies", dealId] });
      if (result.isActive === false) {
        toast({
          title: t("common.success"),
          description: t(
            "deal.companyAddedInactiveWarning",
            "Firma bola pridaná, ale podľa registra nie je aktívna{{status}}.",
            { status: result.status ? ` (${result.status})` : "" }
          ),
          variant: "destructive",
        });
        return;
      }
      toast({
        title: t("common.success"),
        description: t("deal.companyAddedFromIco", "Firma bola pridaná z IČO."),
      });
    },
    onError: (err: Error) => {
      toast({
        title: t("common.error"),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (contactId: string) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/companies/${contactId}`), {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.message || data.error || "Failed to remove");
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-companies", dealId] });
    },
    onError: (err: Error) => {
      toast({
        title: t("common.error"),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const replaceCompanyByIcoMutation = useMutation({
    mutationFn: async ({ oldContactId, newIco }: { oldContactId: string; newIco: string }) => {
      const digits = newIco.replace(/\D/g, "").trim();
      if (digits.length < 8) {
        throw new Error(t("deal.icoInvalid", "Zadajte platné IČO (8–10 číslic)."));
      }

      const lookup = await fetch(apiUrl("/api/crm/finstat-lookup"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ico: digits }),
      });
      const fin = await lookup.json().catch(() => ({}));
      if (!lookup.ok || !fin.success) {
        throw new Error(fin.message || fin.error || t("deal.finstatNotFound", "Firma sa nenašla v registri."));
      }

      const sidlo = typeof fin.sidlo_firmy === "string" ? fin.sidlo_firmy : "";
      const parsed = parseSkSidlo(sidlo);
      const add = await fetch(apiUrl(`/api/crm/deals/${dealId}/companies`), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ico: fin.ico ?? digits,
          companyName: fin.meno_firmy ?? digits,
          address: parsed.street || null,
          city: parsed.city || null,
          country: "Slovensko",
          dic: fin.dic ?? null,
          ic_dph: fin.ic_dph ?? null,
        }),
      });
      const added = await add.json().catch(() => ({}));
      if (!add.ok) {
        throw new Error(added.message || added.error || t("deal.addCompanyFailed", "Nepodarilo sa pridať firmu."));
      }

      const newContactId = added?.company?.id as string | undefined;
      if (newContactId && newContactId !== oldContactId) {
        const del = await fetch(apiUrl(`/api/crm/deals/${dealId}/companies/${oldContactId}`), {
          method: "DELETE",
          credentials: "include",
        });
        if (!del.ok) {
          const data = await del.json().catch(() => ({}));
          throw new Error(data.message || data.error || "Failed to remove old company link");
        }
      }
      return { isActive: fin.is_active !== false, status: fin.status as string | undefined };
    },
    onSuccess: (result) => {
      setEditingCompanyId(null);
      setEditingIco("");
      qc.invalidateQueries({ queryKey: ["deal-companies", dealId] });
      if (result?.isActive === false) {
        toast({
          title: t("common.success"),
          description: t(
            "deal.companyIcoReloadedInactiveWarning",
            "IČO bolo aktualizované, ale podľa registra firma nie je aktívna{{status}}.",
            { status: result.status ? ` (${result.status})` : "" }
          ),
          variant: "destructive",
        });
        return;
      }
      toast({
        title: t("common.success"),
        description: t("deal.companyIcoReloaded", "IČO bolo aktualizované a údaje firmy znovu načítané."),
      });
    },
    onError: (err: Error) => {
      toast({
        title: t("common.error"),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const companies = companiesQuery.data?.companies ?? [];

  return (
    <div className="border-t pt-4 space-y-3">
      <h3 className="font-semibold text-sm flex items-center gap-1.5">
        <Building2 className="h-4 w-4" />
        {t("deal.companiesHeading", "Cégek")}
      </h3>
      {companiesQuery.isLoading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("common.loading")}
        </div>
      ) : companies.length === 0 ? (
        <div className={cn(DEAL_DETAIL_DATA_CARD, "space-y-3")}>
          <p className="text-muted-foreground">
            {t("deal.noCompanies", "K tomuto obchodu zatiaľ nie je prepojená žiadna firma.")}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center max-w-md">
            <Input
              placeholder={t("deal.icoPlaceholder", "IČO")}
              value={icoLoad}
              onChange={(e) => setIcoLoad(e.target.value)}
              className="sm:max-w-[160px]"
              inputMode="numeric"
              autoComplete="off"
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="gap-2"
              disabled={loadFromFinstatMutation.isPending}
              onClick={() => loadFromFinstatMutation.mutate()}
            >
              {loadFromFinstatMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              ) : null}
              {t("deal.loadCompanyFromFinstat", "Načítať údaje firmy")}
            </Button>
          </div>
        </div>
      ) : (
        <ul className="space-y-3">
          {companies.map((c: any) => {
            const addrLine = [c.street, [c.city, c.zip].filter(Boolean).join(" "), c.country]
              .filter(Boolean)
              .join(", ");
            return (
              <li key={c.id} className={cn(DEAL_DETAIL_DATA_CARD, "space-y-0")}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-foreground">{c.name || c.ico || c.id}</div>
                    {c.ico && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {t("deal.icoLabel")}: {c.ico}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 rounded-md border border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setEditingCompanyId(c.id);
                      setEditingIco(c.ico || "");
                    }}
                    disabled={deleteMutation.isPending || replaceCompanyByIcoMutation.isPending}
                    aria-label={t("deal.edit")}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 rounded-md border border-border bg-background hover:bg-muted text-muted-foreground hover:text-destructive"
                    onClick={() => deleteMutation.mutate(c.id)}
                    disabled={deleteMutation.isPending || replaceCompanyByIcoMutation.isPending}
                    aria-label={t("common.remove")}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {editingCompanyId === c.id && (
                    <div className="border-t border-border pt-3 mt-1 space-y-2">
                      <div className="space-y-2">
                        <Input
                          value={editingIco}
                          onChange={(e) => setEditingIco(e.target.value)}
                          placeholder={t("deal.icoPlaceholder", "IČO")}
                          className="w-full"
                          inputMode="numeric"
                          autoComplete="off"
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="gap-2"
                            disabled={replaceCompanyByIcoMutation.isPending}
                            onClick={() => replaceCompanyByIcoMutation.mutate({ oldContactId: c.id, newIco: editingIco })}
                          >
                            {replaceCompanyByIcoMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                            ) : null}
                            {t("deal.reloadCompanyByIco", "Znovu načítať podľa IČO")}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingCompanyId(null);
                              setEditingIco("");
                            }}
                          >
                            {t("common.cancel")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  {addrLine && (
                    <div className="flex justify-between gap-3 items-start">
                      <span className="text-muted-foreground shrink-0">{t("deal.addressLabel", "Adresa")}</span>
                      <span className="text-right font-medium text-foreground break-words min-w-0">{addrLine}</span>
                    </div>
                  )}
                  {c.dic && (
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground shrink-0">DIČ</span>
                      <span className="text-right font-medium text-foreground tabular-nums">{c.dic}</span>
                    </div>
                  )}
                  {c.ic_dph && (
                    <div className="flex justify-between gap-3 items-start">
                      <span className="text-muted-foreground shrink-0">IČ DPH</span>
                      <span className="text-right font-medium text-foreground break-all min-w-0">{c.ic_dph}</span>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
